/**
 * ShareProfileQrCard
 *
 * "Share My Profile" widget on the guide dashboard. Renders the guide's QR
 * code (lazily generated server-side via QrService → ShortCodeService), with
 * download options and a copy-link action. Shows a fallback banner when the
 * branded QR generator (QR Monkey) is unavailable and the API has fallen back
 * to a generic local QR.
 *
 * Epic: MVP 1.37 §5.18
 * Story: SCRUM-1820 — Day-One Consumers (3 pts)
 */
import React, { useEffect, useState } from 'react';
import { Copy, Download, Check, AlertTriangle, X } from 'lucide-react';
import apiRequest from '../../services/api';

const VITE_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

export default function ShareProfileQrCard({ guideId }) {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fallbackDismissed, setFallbackDismissed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloadingPng, setDownloadingPng] = useState(false);

  useEffect(() => {
    if (!guideId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiRequest(`/api/v1/guides/${guideId}/qr`, {
          method: 'GET',
        });
        if (cancelled) {
          return;
        }
        if (response && response.success) {
          setQrData(response.data);
        } else {
          setError('Failed to load QR code');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err && err.message ? err.message : 'Failed to load QR code');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [guideId]);

  const handleDownloadSvg = () => {
    if (!qrData || !qrData.cdnSvgUrl) {
      return;
    }
    const a = document.createElement('a');
    a.href = qrData.cdnSvgUrl;
    a.download = `guide-${guideId}-qr.svg`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadPng = async () => {
    setDownloadingPng(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      const url = `${VITE_API_URL}/api/v1/guides/${guideId}/qr?format=png`;
      const res = await fetch(url, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!res.ok) {
        throw new Error(`Download failed: HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `guide-${guideId}-qr.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err && err.message ? err.message : 'PNG download failed');
    } finally {
      setDownloadingPng(false);
    }
  };

  const handleCopyLink = async () => {
    const profileUrl = `${window.location.origin}/guides/${guideId}`;
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may be unavailable (HTTP, older browsers); silently no-op
    }
  };

  const cardStyle = {
    backgroundColor: 'var(--color-white)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-6)',
    boxShadow: 'var(--shadow-md)',
  };

  if (loading) {
    return (
      <div style={cardStyle} data-testid="share-profile-qr-loading">
        <h3 style={{ fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-4)' }}>
          Share My Profile
        </h3>
        <p style={{ color: 'var(--color-text-secondary)' }}>Loading QR code…</p>
      </div>
    );
  }

  if (error || !qrData) {
    return (
      <div style={cardStyle} data-testid="share-profile-qr-error">
        <h3 style={{ fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-4)' }}>
          Share My Profile
        </h3>
        <p style={{ color: 'var(--color-error)' }}>{error || 'No QR code available'}</p>
      </div>
    );
  }

  const showFallback = qrData.generationMode === 'fallback' && !fallbackDismissed;

  return (
    <div style={cardStyle} data-testid="share-profile-qr-card">
      <h3 style={{
        fontWeight: 'var(--font-weight-semibold)',
        fontSize: 'var(--font-size-lg)',
        marginBottom: 'var(--space-4)',
      }}>
        Share My Profile
      </h3>

      {showFallback && (
        <div
          data-testid="share-profile-qr-fallback-banner"
          style={{
            backgroundColor: 'var(--color-warning-light)',
            color: 'var(--color-warning-dark)',
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-4)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-2)',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
            <AlertTriangle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
            <span style={{ fontSize: 'var(--font-size-sm)' }}>
              {qrData.fallbackMessage ||
                'Branded QR generation is temporarily unavailable; a generic QR code has been generated.'}
            </span>
          </div>
          <button
            onClick={() => setFallbackDismissed(true)}
            aria-label="Dismiss"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'inherit',
              padding: 0,
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-4)' }}>
        {qrData.cdnSvgUrl && (
          <img
            src={qrData.cdnSvgUrl}
            alt="Your profile QR code"
            data-testid="share-profile-qr-image"
            style={{ width: 200, height: 200, border: '1px solid var(--color-border)' }}
          />
        )}

        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={handleDownloadSvg}
            data-testid="share-profile-qr-download-svg"
            style={{
              padding: 'var(--space-2) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-white)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            <Download size={14} /> Download SVG
          </button>

          <button
            onClick={handleDownloadPng}
            disabled={downloadingPng}
            data-testid="share-profile-qr-download-png"
            style={{
              padding: 'var(--space-2) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-white)',
              cursor: downloadingPng ? 'not-allowed' : 'pointer',
              opacity: downloadingPng ? 0.6 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            <Download size={14} /> {downloadingPng ? 'Preparing PNG…' : 'Download PNG'}
          </button>

          <button
            onClick={handleCopyLink}
            data-testid="share-profile-qr-copy-link"
            style={{
              padding: 'var(--space-2) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-white)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied!' : 'Copy Profile Link'}
          </button>
        </div>
      </div>
    </div>
  );
}
